/**
 * Custom hook for video platform API operations
 * Centralizes all API calls with proper authentication
 */

import {
  VideoPlatformResource,
  MeetingCategoryOption,
} from "../../../api/video_platform";
import { CreateMeetingFormData } from "../types";

export const useVideoPlatformApi = () => {
  const createMeeting = async (formData: CreateMeetingFormData) => {
    return VideoPlatformResource.createMeeting(formData);
  };

  const deleteMeeting = async (meetingId: string) => {
    return VideoPlatformResource.deleteMeeting(meetingId);
  };

  const refreshAttendees = (meetingId: string) => {
    return VideoPlatformResource.getMeetingAttendees({ meetingId });
  };

  const updateAttendeeCapabilities = async (
    meetingId: string,
    attendeeId: string,
    capabilities: {
      audio?: { value?: string; lock?: boolean };
      video?: { value?: string; lock?: boolean };
      content?: { value?: string; lock?: boolean };
    }
  ) => {
    return VideoPlatformResource.updateAttendeeCapabilities(
      meetingId,
      attendeeId,
      capabilities
    );
  };

  const batchUpdateAttendeeCapabilities = async (
    meetingId: string,
    operation:
      | "mute_all"
      | "unmute_all"
      | "disable_video_all"
      | "enable_video_all"
      | "disable_content_all"
      | "enable_content_all",
    excludeHosts: boolean = true,
    lock: boolean = false
  ) => {
    return VideoPlatformResource.batchUpdateAttendeeCapabilities(meetingId, {
      operation,
      excludeHosts,
      lock,
    });
  };

  const getCategories = async (): Promise<{
    categories: MeetingCategoryOption[];
  }> => {
    return VideoPlatformResource.getCategories();
  };

  const updateMeeting = async (
    meetingId: string,
    data: {
      category?: string;
      title?: string;
      description?: string;
      isPublicVisible?: boolean;
    }
  ) => {
    return VideoPlatformResource.updateMeeting(meetingId, data);
  };

  return {
    createMeeting,
    deleteMeeting,
    refreshAttendees,
    updateAttendeeCapabilities,
    batchUpdateAttendeeCapabilities,
    getCategories,
    updateMeeting,
  };
};
